import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { toFullName } from '@repo/toolkit/to-full-name'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import { AddUserIcon, ChevronLeftIcon, UserFourIcon, UserRemoveIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Modal, { MODAL_CONTENT_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { API } from '../../api/apis.config'
import { getStaffRequest } from '../../api/staff-requests/get-staff-request'
import CountryFlag from '../../components/elements/country-flag'
import LabelWithText from '../../components/elements/label-with-text'
import LoadingScreen from '../../components/elements/loading/loading-screen'
import UserNavBar from '../../components/elements/nav-bar'
import { QualificationBadge } from '../../components/elements/qualification-badge'
import CancelAffectedStaffModal from '../../components/pages/affected-staffs/cancel-affected-staff/cancel-affected-staff-modal'
import { EmailModal } from '../../components/pages/common/email-modal'
import AffectedStaffTable from '../../components/pages/staff/affected-staffs-table'
import AffectStaff from '../../components/pages/staff-requests/affect-staff'
import { Paths } from '../../lib/constants/path'
import { useObserver } from '../../lib/contexts/observer'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import { AffectedStaffStatusEnum } from '../../lib/types/enums/affected-staff/affected-staff-status.enum'
import { missionStatusEnumMapping } from '../../lib/types/enums/mission/mission-status.enum'
import { getTranslatedName } from '../../lib/utils/functions/translation.functions'
import NotFoundPage from '../common/not-found'

const StaffRequestDetailsPage = () => {
  const { id } = useParams<{ id: string }>()
  const { back } = useNavigator()

  const { modalState, selectedData, toggleModal, closeAll, closeModel } = useModalManager<
    'deactivate' | 'affect-staff' | 'send-affection-email' | 'send-deaffection-email',
    { ids: number[] }
  >(['deactivate', 'affect-staff', 'send-affection-email', 'send-deaffection-email'])

  const { data, isPending, refetch } = useQuery({
    queryKey: ['staff-requests', id],
    queryFn: () => {
      if (!id || isNaN(parseInt(id))) throw new Error('Invalid staff request ID')
      return getStaffRequest(parseInt(id))
    }
  })

  const staffRequestObserver = useObserver({ key: 'staff-requests' })

  const country = data?.country?.code ?? data?.centers.at(0)?.journeyPlace?.country.code
  const affectedStaffs = data?.affectedStaffs.filter(
    (as) => as.status === AffectedStaffStatusEnum.AFFECTED
  )
  const isCenterLocked = data?.centers.at(0)?.locked

  if (isPending) return <LoadingScreen />
  if (!data) return <NotFoundPage />

  return (
    <div>
      <UserNavBar title={'Détail de la demande et encadrants affectés'} icon={UserFourIcon} />
      <div className={'h-4'} />
      <Box className="flex flex-col gap-4">
        <Button
          icon={ChevronLeftIcon}
          label="Retour"
          variant="secondary"
          className="flex justify-start w-fit"
          onClick={() => back()}
        />
        <InnerBox className="rounded-xl flex flex-col gap-4">
          <div className="flex gap-4 items-center">
            <Typography variant={'body-medium'} color={tailwindColors.primary.DEFAULT}>
              Demande
            </Typography>
            <Badge
              text={missionStatusEnumMapping[data.status].label}
              variant={missionStatusEnumMapping[data.status].variant}
            />
          </div>
          <Box className="bg-primary-light grid grid-cols-6 gap-2">
            <LabelWithText label="Pays" data={country ? <CountryFlag code={country} /> : '-'} />
            <LabelWithText
              label="Centre"
              data={
                data.centers.length > 0
                  ? data.centers.map((center) => center.name).join(', ')
                  : data.oldCenter || '-'
              }
            />
            <LabelWithText
              label="Formule"
              data={
                data.formulas.length > 0
                  ? data.formulas.map((formula) => getTranslatedName(formula)).join(', ')
                  : '-'
              }
            />
            <LabelWithText
              label="Options"
              data={
                data.centers && data.centers.length > 0
                  ? data.centers
                      .flatMap((center) =>
                        center.centerToJourneys.map((d) => getTranslatedName(d.journey.mainOption))
                      )
                      .join(', ')
                  : data.oldMainOption || '-'
              }
            />
            <LabelWithText
              label="Dates"
              data={
                data.startDate && data.endDate ? (
                  <Badge
                    text={`${DateFormatter.DDMMYYYY(data.startDate)} - ${DateFormatter.DDMMYYYY(data.endDate)}`}
                  />
                ) : (
                  '-'
                )
              }
            />
            <LabelWithText label="Durée (jours)" data={data.duration ?? '-'} />
            <LabelWithText
              label="Qualification"
              data={<QualificationBadge qualification={data.qualification} />}
            />
            <LabelWithText label="# Demandés" data={data.numberOfStaff} />
            <LabelWithText
              label="# Affectés"
              data={valueRender(data.affectedStaffs, (staffs) => `${staffs.length}`)}
            />
            <LabelWithText
              label="Noms des encadrants affectés"
              className="text-nowrap"
              data={
                affectedStaffs && affectedStaffs.length > 0
                  ? affectedStaffs
                      .map((as) =>
                        toFullName(as.staff.firstName, as.staff.lastName, {
                          options: { mode: 'lastname-firstname' }
                        })
                      )
                      .join(', ')
                  : '-'
              }
            />
          </Box>
        </InnerBox>
        <InnerBox className="rounded-xl p-0">
          <AffectedStaffTable
            filterOptions={{
              selectedFilter: {
                staffRequestId: data.id,
                statuses: [AffectedStaffStatusEnum.AFFECTED, AffectedStaffStatusEnum.PENDING]
              }
            }}
            allowSearchParam={false}
            actions={() => [
              {
                type: 'global',
                icon: AddUserIcon,
                label: 'Affecter',
                variant: 'ghost',
                refetch: true,
                fn: () => toggleModal('affect-staff')
              },
              {
                type: 'multiple',
                icon: UserRemoveIcon,
                label: 'Désaffecter',
                variant: 'destructive',
                fn: (staff) => {
                  toggleModal('deactivate', { ids: staff.map((s) => s.id) })
                }
              }
            ]}
          />
        </InnerBox>
      </Box>

      {/* Send email */}
      {selectedData && (
        <CancelAffectedStaffModal
          affectedStaffIds={selectedData.ids}
          isOpen={modalState.deactivate}
          onCancel={() => toggleModal('deactivate')}
          onSuccess={async () => {
            // toggle the send email modal when the center is locked
            if (isCenterLocked) toggleModal('send-deaffection-email')

            await refetch()

            // if the staff request has 0 requested staff and no affected staffs, redirect to list page
            if (
              data.numberOfStaff === 0 &&
              data.affectedStaffs.length === selectedData.ids.length
            ) {
              toast.success(
                "Cette demande ne comporte plus d'encadrant affecté ni de besoin en encadrant, vous allez être redirigé vers la liste des demandes."
              )
              back(Paths.STAFF_REQUEST_LIST_PAGE)
            }
          }}
        />
      )}

      <Modal
        isOpen={modalState['affect-staff']}
        title="Affecter un encadrant"
        size="large"
        hideActions
        icon={AddUserIcon}
        onCancel={() => toggleModal('affect-staff')}
      >
        <AffectStaff
          className={MODAL_CONTENT_CLASSNAME}
          staffRequestId={data.id}
          toggleModal={() => toggleModal('affect-staff')}
          onSave={() => {
            staffRequestObserver.notify(['staff-requests', 'affected-staffs']) // notify the observer to refetch the staff request data
            refetch()

            // if the center is locked, we toggle the send email modal to notify the admins, otherwise we just close the affect staff modal
            if (isCenterLocked) toggleModal('send-affection-email')

            closeModel('affect-staff')
          }}
        />
      </Modal>

      <EmailModal
        emailApiEndpoint={API.affectedStaffs.sendAffectedStaffAffectionNotification.url}
        label="Vous avez affecté un encadrant à un centre verrouillé. Souhaitez-vous envoyer une notification ?"
        isOpen={modalState['send-affection-email']}
        closeModal={() => closeAll()}
      />

      <EmailModal
        emailApiEndpoint={API.affectedStaffs.sendAffectedStaffDeaffectionNotification.url}
        label="Vous vous apprêtez à désaffecter un encadrant sur un centre verrouillé. Souhaitez-vous envoyer une notification ?"
        isOpen={modalState['send-deaffection-email']}
        closeModal={() => closeAll()}
      />
    </div>
  )
}

export default StaffRequestDetailsPage
